import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Container, Row } from "react-bootstrap";
import { getFormBaseValidationRule } from "../../utils/form/form-utils";
import EzControlledDropDowntWithFloatingLabel from "../../common/EzForm/EzControlledDropDown/WithFloatingLabel";
import { Divider } from "primereact/divider";
import { fuelProviders } from "../../config/fuelProvidersConfig";
import { Calendar } from "primereact/calendar";

const DailyRates = (props) => {
  const { uploadDailyRates, uploadAllUSDRates } = props;
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    clearErrors,
    watch,
  } = useForm();
  const [selectedFile, setSelectedFile] = useState(null);
  const [taPetroFile, setTaPetroFile] = useState(null);
  const [pilotFile, setPilotFile] = useState(null);
  const [priceDate, setPriceDate] = useState(null);
  const fileUploadRef = useRef(null);
  const taPetroFileRef = useRef(null);
  const pilotFileRef = useRef(null);

  const selectedProvider = watch("fuelProvider");

  const onSubmit = (data) => {
    // Format priceDate to YYYY-MM-DD if selected
    const formattedDate = priceDate 
      ? priceDate.toISOString().split('T')[0] 
      : null;

    if (data.fuelProvider === "ALL-USD") {
      // For ALL-USD, both TA-Petro and Pilot files are mandatory
      if (!taPetroFile) {
        setError("ta_petro_file", {
          type: "manual",
          message: "TA-Petro file is required for ALL-USD",
        });
      }
      if (!pilotFile) {
        setError("pilot_file", {
          type: "manual",
          message: "Pilot file is required for ALL-USD",
        });
      }

      if (!taPetroFile || !pilotFile) {
        return;
      }

      const payload = {
        type: data.fuelProvider,
        ta_petro_file: taPetroFile,
        pilot_file: pilotFile,
        price_date: formattedDate,
      };
      uploadDailyRates(payload);
    } else {
      if (!selectedFile) {
        setError("file", { type: "manual", message: "File is required" });
        return;
      }
      const payload = {
        type: data.fuelProvider,
        file: selectedFile,
        price_date: formattedDate,
      };
      uploadDailyRates(payload);
    }
    handleReset();
  };

  const handleReset = () => {
    reset();
    setSelectedFile(null);
    setTaPetroFile(null);
    setPilotFile(null);
    setPriceDate(null);
    clearErrors(["file", "ta_petro_file", "pilot_file"]);
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
    if (taPetroFileRef.current) {
      taPetroFileRef.current.clear();
    }
    if (pilotFileRef.current) {
      pilotFileRef.current.clear();
    }
  };

  const getFormErrorMessage = (name) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  return (
    <div className="paddingbox">
      <div className="containerbox">
        <form className="p-fluid" onSubmit={handleSubmit(onSubmit)}>
          <SectionHeader label="Upload Daily Rates" />

          <Container fluid className="card-box">
            <div className="my-3 w-50">
              <EzControlledDropDowntWithFloatingLabel
                name="fuelProvider"
                rules={
                  getFormBaseValidationRule().DEFAULT.VALIDATION_RULE_MESSAGE
                }
                control={control}
                optionLabel="value"
                optionValue="key"
                options={fuelProviders}
                label="Select the fuel provider"
                isError={errors["fuelProvider"]}
                errorMsg={getFormErrorMessage("fuelProvider")}
              />
            </div>

            {selectedProvider === "ALL-USD" ? (
              <div className="container-fluid">
                  <div className="row mb-3 align-items-center">
                    <div className="col-md-2 col-12 text-md-end">
                      <label className="form-label fw-bold mb-0">Pilot File:*</label>
                    </div>
                    <div className="col-md-4 col-12">
                      <FileUpload
                        ref={pilotFileRef}
                        mode="basic"
                        customUpload
                        maxFileSize={1000000}
                        accept=".xls,.xlsx"
                        chooseLabel="Select Pilot File"
                        className="w-100"
                        onSelect={(e) => {
                          if (e.files && e.files.length > 0) {
                            setPilotFile(e.files[0]);
                            clearErrors("pilot_file");
                          }
                        }}
                      />
                      {getFormErrorMessage("pilot_file")}
                    </div>
                  </div>

                  <div className="row mb-3 align-items-center">
                    <div className="col-md-2 col-12 text-md-end">
                      <label className="form-label fw-bold mb-0">TA-Petro File:*</label>
                    </div>
                    <div className="col-md-4 col-12">
                      <FileUpload
                        ref={taPetroFileRef}
                        mode="basic"
                        customUpload
                        maxFileSize={1000000}
                        accept=".xls,.xlsx"
                        chooseLabel="Select TA-Petro File"
                        className="w-100"
                        onSelect={(e) => {
                          if (e.files && e.files.length > 0) {
                            setTaPetroFile(e.files[0]);
                            clearErrors("ta_petro_file");
                          }
                        }}
                      />
                      {getFormErrorMessage("ta_petro_file")}
                    </div>
                  </div>

                  <div className="row mb-3 align-items-center">
                    <div className="col-md-2 col-12 text-md-end">
                      <label className="form-label fw-bold mb-0">Price Date:</label>
                    </div>
                    <div className="col-md-4 col-12">
                      <Calendar
                        value={priceDate}
                        onChange={(e) => setPriceDate(e.value)}
                        dateFormat="yy-mm-dd"
                        placeholder="Select date (defaults to today)"
                        showIcon
                        className="w-100"
                      />
                    </div>
                  </div>
                </div>
            ) : (
              <div className="my-3 w-50">
                <label className="form-label">Upload File</label>
                <FileUpload
                  ref={fileUploadRef}
                  mode="basic"
                  customUpload
                  maxFileSize={1000000}
                  accept=".xls,.xlsx"
                  chooseLabel="Select File"
                  onSelect={(e) => {
                    if (e.files && e.files.length > 0) {
                      setSelectedFile(e.files[0]);
                      clearErrors("file");
                    }
                  }}
                />
                {getFormErrorMessage("file")}

                <div className="mt-3">
                  <label className="form-label">Price Date</label>
                  <Calendar
                    value={priceDate}
                    onChange={(e) => setPriceDate(e.value)}
                    dateFormat="yy-mm-dd"
                    placeholder="Select date (defaults to today)"
                    showIcon
                    className="w-100"
                  />
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end mt-3">
              <Button
                label="Reset"
                type="button"
                className="p-btn-danger p-button-outlined mr-4"
                onClick={handleReset}
                style={{ width: "100px" }}
              />
              <Button
                type="submit"
                label="Send"
                className="p-button-raised"
                style={{ width: "100px" }}
              />
            </div>
          </Container>
        </form>
      </div>
    </div>
  );
};

const SectionHeader = ({ label }) => (
  <Row>
    <div className="d-flex w-100">
      <b className="ml-2">{label || ""}</b>
    </div>
    <Divider align="top" type="solid" />
  </Row>
);

export default DailyRates;
